/* Two-pane career picker: pick categories on the left, see score-gated jobs on
   the right (with Expand-all / Qualify-only toggles). Jobs above your RealU
   Score are locked. */
import { useRef, useEffect, useState, useCallback } from 'react';
import { CATEGORIES, Job } from '../data';
import { Icon } from '../icons';
import { money } from '../util';
import { useSurvey } from '../SurveyContext';

export function CareerPicker() {
  const surveyFlow = useSurvey();
  const sc = surveyFlow.score;
  const cats = CATEGORIES;
  const sel = surveyFlow.answers.careerCategories || [];
  const expandAll = !!surveyFlow.answers._careerExpandAll;
  const qualOnly = !!surveyFlow.answers._careerQualOnly;
  const allCatIds = cats.map((c) => c.id);
  const selKey = sel.join(',');

  const listRef = useRef<HTMLDivElement>(null);
  const [scrollMax, setScrollMax] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [thumbPx, setThumbPx] = useState(0);

  const syncScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setScrollMax(max);
    setScrollLeft(el.scrollLeft);
    setThumbPx(max > 0 ? Math.round(el.clientWidth * el.clientWidth / el.scrollWidth) : el.clientWidth);
  }, []);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    syncScroll();
    el.addEventListener('scroll', syncScroll, { passive: true });
    const ro = new ResizeObserver(syncScroll);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', syncScroll); ro.disconnect(); };
  }, [syncScroll]);

  // Re-sync when visible categories change (scrollWidth changes, ResizeObserver won't catch it)
  useEffect(() => { syncScroll(); }, [selKey, expandAll, qualOnly, syncScroll]);

  // Expand-all shows every category; otherwise only the selected ones. (Qualify-only
  // selects every category when toggled on — see the toggle handler below — so it
  // no longer needs a special case here.)
  const showCats = expandAll ? cats.slice() : cats.filter((c) => sel.includes(c.id));

  const jobBtn = (j: Job, c: (typeof CATEGORIES)[number]) => {
    const locked = j.req > sc;
    return (
      <button type="button"
        key={c.id + ':' + j.title}
        className="intake-job"
        data-locked={locked ? 1 : 0}
        aria-pressed={!!(surveyFlow.answers.career && surveyFlow.answers.career.title === j.title)}
        onClick={locked ? undefined : () => surveyFlow.setA({ career: j })}
      >
        <div>
          <div className="intake-jt">{j.title}</div>
          <div className="intake-jm">Score needed: {j.req.toFixed(2)}</div>
        </div>
        {locked ? (
          <span className="intake-badge">
            <Icon name="lock" size={14} /> {j.req.toFixed(2)}
          </span>
        ) : (
          <span className="intake-sal">
            {money(j.salary)}
            <div className="intake-jm" style={{ textAlign: 'right', fontWeight: 600 }}>
              / yr
            </div>
          </span>
        )}
      </button>
    );
  };

  const groups = showCats
    .map((c) => {
      let jobs = c.jobs.slice();
      // Expand-all wins over Qualify-only: with both on, show every job
      // (locked ones included) instead of silently filtering.
      if (qualOnly && !expandAll) jobs = jobs.filter((j) => j.req <= sc);
      // Jobs the student qualifies for (req <= score) first, locked ones below.
      // Stable sort keeps each tier in its incoming (gpa-desc) order.
      jobs.sort((a, b) => (a.req > sc ? 1 : 0) - (b.req > sc ? 1 : 0));
      if (!jobs.length) return null;
      return (
        <div className="intake-jobgroup" key={c.id}>
          <div className="intake-jobhead">
            <span>
              <Icon name={c.icon} size={15} /> {c.label}
            </span>
          </div>
          <div className="intake-opts intake-joblist">{jobs.map((j) => jobBtn(j, c))}</div>
        </div>
      );
    })
    .filter(Boolean);

  return (
    <div>
      <div className="intake-jobpane">
        <span className="intake-scorehint">Your score: {sc.toFixed(2)}</span>
        <div className="intake-jobtools">
          <div className="intake-toolbtns">
            <button type="button"
              className="intake-toolbtn"
              aria-pressed={expandAll}
              onClick={() =>
                surveyFlow.setA(
                  expandAll
                    ? { _careerExpandAll: false, careerCategories: qualOnly ? allCatIds : [] }
                    : { _careerExpandAll: true, careerCategories: allCatIds }
                )
              }
            >
              <Icon name="book" size={14} /> Expand All Jobs
            </button>
            <button type="button"
              className="intake-toolbtn"
              aria-pressed={qualOnly}
              onClick={() =>
                surveyFlow.setA(
                  qualOnly
                    ? { _careerQualOnly: false, careerCategories: expandAll ? allCatIds : [] }
                    : { _careerQualOnly: true, careerCategories: allCatIds }
                )
              }
            >
              <Icon name="trophy" size={14} /> Jobs I Qualify For
            </button>
          </div>
        </div>
      </div>
      <div className="intake-grid">
        {cats.map((c) => {
          const on = sel.includes(c.id);
          return (
            <button type="button"
              key={c.id}
              className="intake-cat"
              aria-pressed={on}
              onClick={() => surveyFlow.toggleCat(c.id)}
            >
              <span className="intake-ci">
                <Icon name={c.icon} size={25} />
              </span>
              <div>
                <div className="intake-nm">{c.label}</div>
                <div className="intake-sk">{c.skills}</div>
              </div>
            </button>
          );
        })}
      </div>
      {groups.length ? (
        <div className="intake-jobscroll-wrap">
          {scrollMax > 0 && (
            <input
              type="range"
              className="intake-scrollbar"
              min={0}
              max={scrollMax}
              value={scrollLeft}
              style={{ '--thumb-px': thumbPx + 'px' } as React.CSSProperties}
              onChange={(e) => {
                const val = Number(e.target.value);
                const el = listRef.current;
                if (!el) return;
                el.scrollLeft = val;
                setScrollLeft(val);
              }}
            />
          )}
          <div className="intake-joblist" ref={listRef}>
            {groups}
          </div>
        </div>
      ) : (
        <div className="intake-jobempty">
          <span>No jobs match yet. Pick a category.</span>
        </div>
      )}
    </div>
  );
}
